/**
 * "Who's watching?" — profile picker shown after activation, before the menu.
 * Allows creating up to 5 profiles. Each profile has its own watch history & favorites.
 */
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  listProfiles,
  createProfile,
  deleteProfile,
  setActiveProfile,
  getActiveProfile,
  verifyProfilePin,
  hasPin,
  setProfilePin,
  refreshProfiles,
  AVATAR_COLORS,
  MAX_PROFILES,
} from "../lib/profiles";
import { getAssignedPlaylists, claimProfileApi, releaseProfileApi } from "../lib/playerApi";
import { maybeRestoreProfileCache } from "../lib/profileCacheSync";
import { useDevice } from "../lib/DeviceContext";
import { Plus, Trash2, X, AlertTriangle, ShieldCheck, Lock, KeyRound, Loader2 } from "lucide-react";
import "../vu-theme.css";

const STORAGE_NOTICE_KEY = "vupp_profile_notice_seen";

export default function ProfileSelect() {
  const navigate = useNavigate();
  const { panelName } = useDevice();
  const [profiles, setProfiles] = useState(() => listProfiles());
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [playlists, setPlaylists] = useState([]);
  const [activePlaylistId, setActivePlaylistIdState] = useState(
    () => localStorage.getItem("vupp_active_playlist") || null
  );
  const [manage, setManage] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [showNotice, setShowNotice] = useState(false);
  const [name, setName] = useState("");
  const [avatarIdx, setAvatarIdx] = useState(0);
  const [isKids, setIsKids] = useState(false);
  const [pin, setPin] = useState("");
  const [error, setError] = useState("");

  // PIN gate (when switching out of a kids profile to a PIN-protected one)
  const [pinGateProfile, setPinGateProfile] = useState(null);
  const [pinAttempt, setPinAttempt] = useState("");
  const [pinError, setPinError] = useState("");

  // v1.3.72 — "profile in use" modal. Shown when the backend's
  // claim endpoint returns 409 because another device already
  // holds an active lease on the profile.
  // v1.3.80 — added `inUseHolderTag` (short device suffix chip),
  // `inUseUntil` (Date the lock naturally expires) and a "Try again"
  // countdown so the user isn't left guessing.
  const [inUseProfile, setInUseProfile] = useState(null);
  const [inUseMsg, setInUseMsg] = useState("");
  const [inUseHolderTag, setInUseHolderTag] = useState("");
  const [inUseUntil, setInUseUntil] = useState(null);
  const [inUseTick, setInUseTick] = useState(0);
  const [inUseRetrying, setInUseRetrying] = useState(false);

  // Set / change PIN dialog (used from Manage mode)
  const [managePinProfile, setManagePinProfile] = useState(null);
  const [managePinValue, setManagePinValue] = useState("");
  const [managePinError, setManagePinError] = useState("");

  // Load assigned playlists + sync profiles on mount
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await getAssignedPlaylists();
        const pls = r.playlists || [];
        if (cancelled) return;
        setPlaylists(pls);

        // Pick a playlist: stored active, or the first assigned
        let pid = localStorage.getItem("vupp_active_playlist");
        if (!pls.some((p) => p.id === pid)) pid = pls[0]?.id || null;
        if (pid) {
          localStorage.setItem("vupp_active_playlist", pid);
          setActivePlaylistIdState(pid);
        }
        const fresh = await refreshProfiles();
        if (!cancelled) setProfiles(fresh);
        // v1.3.36 — silently restore the favorites + watch history +
        // app settings cached server-side for this playlist if the
        // local copy is empty (typical for a fresh install or a
        // factory-reset device). Per-key, never clobbers data the user
        // already has locally.
        if (pid && !cancelled) maybeRestoreProfileCache(pid);
      } catch {
        /* ignore - cache fallback */
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const refresh = async () => {
    const fresh = await refreshProfiles();
    setProfiles(fresh);
  };

  // v1.3.80 — Parse the 6-char device tag out of the backend's 409
  // "…AABBCC" string so we can render it as a monospace chip.
  const _extractHolderTag = (msg) => {
    if (!msg) return "";
    const m = String(msg).match(/…([A-F0-9]{2,12})/i);
    return m ? m[1].toUpperCase() : "";
  };

  // v1.3.80 — Show the "profile in use" modal with the derived
  // holder tag and a 5-minute retry countdown pinned to when the
  // backend rejected the claim.
  const _openInUse = (profile, detail) => {
    const msg = detail || "This profile is currently in use on another device.";
    setInUseProfile(profile);
    setInUseMsg(msg);
    setInUseHolderTag(_extractHolderTag(msg));
    // Backend `PROFILE_CLAIM_TIMEOUT_S = 300`.
    setInUseUntil(new Date(Date.now() + 5 * 60 * 1000));
    setInUseRetrying(false);
  };

  const _closeInUse = () => {
    setInUseProfile(null);
    setInUseMsg("");
    setInUseHolderTag("");
    setInUseUntil(null);
    setInUseRetrying(false);
  };

  // v1.3.80 — Retry the claim from inside the modal so the user
  // doesn't have to re-tap the tile after the 5-min window.
  const retryInUseClaim = async () => {
    if (!inUseProfile || inUseRetrying) return;
    setInUseRetrying(true);
    try {
      await claimProfileApi(inUseProfile.id);
      const target = inUseProfile;
      _closeInUse();
      // Release the previously-active profile before switching.
      const prev = getActiveProfile();
      if (prev && prev.id !== target.id) {
        releaseProfileApi(prev.id).catch(() => {});
      }
      setActiveProfile(target.id);
      navigate("/player/menu", { replace: true });
    } catch (e) {
      if (e?.response?.status === 409) {
        setInUseMsg(
          e.response.data?.detail || inUseMsg,
        );
        setInUseHolderTag(_extractHolderTag(e.response.data?.detail || inUseMsg));
        // Reset the countdown to the new backend rejection moment.
        setInUseUntil(new Date(Date.now() + 5 * 60 * 1000));
      } else {
        // Non-409 = network/server hiccup → let the user through so
        // they aren't stuck behind a modal because our lock service
        // blipped.
        const target = inUseProfile;
        _closeInUse();
        setActiveProfile(target.id);
        navigate("/player/menu", { replace: true });
      }
    } finally {
      setInUseRetrying(false);
    }
  };

  // v1.3.80 — 1-Hz tick while the in-use modal is open so the
  // countdown label re-renders once per second.
  useEffect(() => {
    if (!inUseUntil) return undefined;
    const id = setInterval(() => setInUseTick((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, [inUseUntil]);

  // v1.3.80 — Escape closes the modal (keyboard + D-pad "Back" both
  // fire keydown Escape on Smart-TV browsers).
  useEffect(() => {
    if (!inUseProfile) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") _closeInUse();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [inUseProfile]);

  const _inUseSecondsLeft = inUseUntil
    ? Math.max(0, Math.ceil((inUseUntil.getTime() - Date.now()) / 1000))
    : 0;
  // Suppress unused-var warning for the tick counter — we only use it
  // to trigger a re-render at 1 Hz so the countdown label refreshes.
  void inUseTick;

  // Profiles shown for the currently-selected playlist (sync target).
  // If no playlist yet (e.g. nothing assigned), fall back to the empty list.
  const visibleProfiles = activePlaylistId
    ? profiles.filter((p) => p.playlist_id === activePlaylistId)
    : [];

  // Auto-skip when only one profile exists and notice already shown:
  // (we always show the picker so the user can switch, but we still allow direct entry)
  // No auto-skip — feature is "always show picker"

  const onPick = async (p) => {
    // PIN gate: leaving a kids profile into a PIN-protected one requires the PIN.
    const current = getActiveProfile();
    if (current && current.is_kids && !p.is_kids && hasPin(p)) {
      setPinGateProfile(p);
      setPinAttempt("");
      setPinError("");
      return;
    }
    // v1.3.72 — claim the profile lease before activating. If
    // another device is currently holding it we surface a modal
    // explaining the situation; the user can pick a different
    // profile or create a new one.
    // v1.3.80 — also release the currently-active profile's lease
    // so the OTHER device (or a future re-pick) sees it free
    // immediately instead of waiting out the 5-minute timeout.
    try {
      await claimProfileApi(p.id);
    } catch (e) {
      if (e?.response?.status === 409) {
        _openInUse(p, e.response.data?.detail);
        return;
      }
      // Any other backend error — log and continue. We don't block
      // the user just because the lock service is temporarily down.
      console.warn("[profile-claim] non-fatal:", e?.message || e);
    }
    // Release the OLD profile's claim so switching feels instant
    // on the other side. Fire-and-forget — server is idempotent.
    if (current && current.id !== p.id) {
      releaseProfileApi(current.id).catch(() => {});
    }
    setActiveProfile(p.id);
    navigate("/player/menu", { replace: true });
  };

  const submitPinGate = async (e) => {
    e?.preventDefault();
    if (!pinGateProfile) return;
    setPinError("");
    const ok = await verifyProfilePin(pinGateProfile.id, pinAttempt);
    if (ok) {
      const target = pinGateProfile;
      setPinGateProfile(null);
      setPinAttempt("");
      // Claim before activating (same flow as above).
      try {
        await claimProfileApi(target.id);
      } catch (err) {
        if (err?.response?.status === 409) {
          _openInUse(target, err.response.data?.detail);
          return;
        }
      }
      // v1.3.80 — release the previously-active profile too.
      const prev = getActiveProfile();
      if (prev && prev.id !== target.id) {
        releaseProfileApi(prev.id).catch(() => {});
      }
      setActiveProfile(target.id);
      navigate("/player/menu", { replace: true });
    } else {
      setPinError("Wrong PIN. Try again.");
      setPinAttempt("");
    }
  };

  const onOpenCreate = () => {
    if (profiles.length >= MAX_PROFILES) return;
    setError("");
    setName("");
    setAvatarIdx(profiles.length % AVATAR_COLORS.length);
    setIsKids(false);
    setPin("");
    setShowCreate(true);
  };

  const onSubmitCreate = async (e) => {
    e?.preventDefault();
    setError("");
    if (!activePlaylistId) {
      setError("No playlist assigned to this device yet. Ask your provider.");
      return;
    }
    setBusy(true);
    try {
      const p = await createProfile({
        name,
        avatarIndex: avatarIdx,
        isKids,
        pin: isKids ? "" : pin,
        playlistId: activePlaylistId,
      });
      await refresh();
      setShowCreate(false);
      // Show the legal notice only the first time a profile is ever created on this device,
      // and also pick the newly created profile after acknowledgement.
      const seen = localStorage.getItem(STORAGE_NOTICE_KEY) === "1";
      if (!seen) {
        setShowNotice(true);
        setPendingPick(p);
      } else {
        onPick(p);
      }
    } catch (err) {
      setError(err?.response?.data?.detail || err.message || "Could not create profile");
    } finally {
      setBusy(false);
    }
  };

  const [pendingPick, setPendingPick] = useState(null);

  const acknowledgeNotice = () => {
    localStorage.setItem(STORAGE_NOTICE_KEY, "1");
    setShowNotice(false);
    if (pendingPick) {
      const p = pendingPick;
      setPendingPick(null);
      onPick(p);
    }
  };

  const onDelete = async (e, p) => {
    e.stopPropagation();
    if (!window.confirm(`Delete profile "${p.name}"? This removes it for all devices using this playlist.`)) return;
    setBusy(true);
    try {
      await deleteProfile(p.id);
      await refresh();
    } catch (err) {
      window.alert(err?.response?.data?.detail || err.message);
    } finally {
      setBusy(false);
    }
  };

  const onOpenManagePin = (e, p) => {
    e.stopPropagation();
    setManagePinProfile(p);
    setManagePinValue("");
    setManagePinError("");
  };

  const submitManagePin = async (e, action = "save") => {
    e?.preventDefault();
    if (!managePinProfile) return;
    setBusy(true);
    try {
      if (action === "clear") {
        await setProfilePin(managePinProfile.id, "");
      } else {
        await setProfilePin(managePinProfile.id, managePinValue);
      }
      setManagePinProfile(null);
      setManagePinValue("");
      setManagePinError("");
      await refresh();
    } catch (err) {
      setManagePinError(err?.response?.data?.detail || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="vu-theme min-h-screen bg-[#07080b] text-slate-100 flex items-center justify-center px-6 py-16 relative overflow-hidden">
      <div className="absolute -top-40 left-1/4 w-[600px] h-[600px] bg-orange-500/10 blur-[140px] rounded-full" />
      <div className="absolute -bottom-40 right-1/4 w-[600px] h-[600px] bg-fuchsia-500/10 blur-[140px] rounded-full" />

      <div className="relative w-full max-w-4xl text-center">
        <p className="text-xs uppercase tracking-[0.25em] text-orange-400 mb-2">
          {/* v1.3.34 — strip the " ADMIN" suffix that the admin's branding
              config carries through, per admin request. Falls back to the
              raw panel name (unchanged) when there is no suffix. */}
          {(panelName || "ZA Player Pro").replace(/\s*ADMIN\s*$/i, "")}
        </p>
        <h1 className="text-4xl sm:text-5xl font-bold tracking-tight mb-2">Who&apos;s watching?</h1>
        <p className="text-sm text-slate-400 mb-12">
          Pick a profile to load its history and favorites.
          {playlists.length > 0 && " Profiles are synced with anyone using the same playlist."}
          {" "}
          {visibleProfiles.length > 0 && (
            <button
              onClick={() => setManage((m) => !m)}
              className="text-orange-300 hover:text-orange-200 underline ml-1"
            >
              {manage ? "Done" : "Manage"}
            </button>
          )}
        </p>

        {loading ? (
          <div className="flex items-center justify-center py-20 text-slate-400">
            <Loader2 className="w-6 h-6 animate-spin" />
          </div>
        ) : (
        <>
        <div className="flex flex-wrap items-start justify-center gap-6 sm:gap-8">
          {visibleProfiles.map((p) => {
            const c = AVATAR_COLORS[p.avatar_index ?? 0] || AVATAR_COLORS[0];
            const initials = p.name
              .split(" ")
              .map((s) => s[0])
              .join("")
              .slice(0, 2)
              .toUpperCase();
            return (
              <button
                key={p.id}
                data-testid={`profile-tile-${p.id}`}
                onClick={() => (manage ? null : onPick(p))}
                className="group flex flex-col items-center gap-3 outline-none relative"
              >
                <div
                  className={`relative w-28 h-28 sm:w-32 sm:h-32 rounded-2xl bg-gradient-to-br ${c.from} ${c.to} flex items-center justify-center text-3xl sm:text-4xl font-bold text-white shadow-xl transition-all ${
                    manage ? "" : "group-hover:scale-105 group-hover:shadow-2xl"
                  } ${p.is_kids ? "ring-4 ring-cyan-400 ring-offset-2 ring-offset-[#07080b]" : ""}`}
                >
                  <span>{initials}</span>
                  {p.is_kids && (
                    <span className="absolute -bottom-2 left-1/2 -translate-x-1/2 bg-cyan-400 text-black text-[10px] font-bold tracking-wide rounded-full px-2 py-0.5 shadow-lg flex items-center gap-1">
                      <ShieldCheck className="w-3 h-3" /> KIDS
                    </span>
                  )}
                  {hasPin(p) && !p.is_kids && (
                    <span
                      className="absolute top-2 left-2 w-6 h-6 rounded-full bg-black/70 border border-white/20 flex items-center justify-center"
                      title="PIN-protected"
                    >
                      <Lock className="w-3 h-3 text-amber-300" />
                    </span>
                  )}
                  {manage && (
                    <>
                      <button
                        onClick={(e) => onDelete(e, p)}
                        data-testid={`profile-delete-${p.id}`}
                        className="absolute -top-2 -right-2 w-7 h-7 rounded-full bg-red-500 hover:bg-red-600 text-white flex items-center justify-center shadow-lg"
                        title="Delete"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                      {!p.is_kids && (
                        <button
                          onClick={(e) => onOpenManagePin(e, p)}
                          data-testid={`profile-pin-${p.id}`}
                          className="absolute -top-2 -left-2 w-7 h-7 rounded-full bg-amber-400 hover:bg-amber-300 text-black flex items-center justify-center shadow-lg"
                          title="Set / change PIN"
                        >
                          <KeyRound className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </>
                  )}
                </div>
                <div className="text-sm font-medium text-slate-200 group-hover:text-white max-w-[140px] truncate">
                  {p.name}
                </div>
              </button>
            );
          })}

          {/* Add Profile tile */}
          {visibleProfiles.length < MAX_PROFILES && !manage && (
            <button
              data-testid="profile-add-tile"
              onClick={onOpenCreate}
              className="group flex flex-col items-center gap-3"
            >
              <div className="w-28 h-28 sm:w-32 sm:h-32 rounded-2xl border-2 border-dashed border-slate-600 group-hover:border-orange-400 flex items-center justify-center text-slate-500 group-hover:text-orange-400 transition-colors">
                <Plus className="w-10 h-10" />
              </div>
              <div className="text-sm font-medium text-slate-400 group-hover:text-white">
                Add profile
              </div>
            </button>
          )}
        </div>

        <p className="text-xs text-slate-500 mt-10">
          {visibleProfiles.length} / {MAX_PROFILES} profiles
          {activePlaylistId && (
            <span className="block mt-1 text-[10px] text-slate-600">
              Synced via playlist · {playlists.find((p) => p.id === activePlaylistId)?.name || ""}
            </span>
          )}
        </p>
        </>
        )}
      </div>

      {/* Create Profile dialog */}
      {showCreate && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm px-4">
          <div className="bg-[#13161d] border border-white/10 rounded-2xl shadow-2xl w-full max-w-sm p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Add a profile</h3>
              <button
                onClick={() => setShowCreate(false)}
                className="p-1 rounded hover:bg-white/10 text-slate-400"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <form onSubmit={onSubmitCreate} className="space-y-4">
              <div>
                <label className="block text-xs uppercase tracking-wider text-slate-400 mb-2">
                  Name
                </label>
                <input
                  autoFocus
                  data-testid="profile-name-input"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="e.g. Living Room"
                  maxLength={24}
                  className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2.5 text-white placeholder:text-slate-600 focus:outline-none focus:border-orange-500/60"
                />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-wider text-slate-400 mb-2">
                  Avatar colour
                </label>
                <div className="grid grid-cols-7 gap-2">
                  {AVATAR_COLORS.map((c, i) => (
                    <button
                      key={i}
                      type="button"
                      data-testid={`profile-avatar-${i}`}
                      onClick={() => setAvatarIdx(i)}
                      className={`aspect-square rounded-lg bg-gradient-to-br ${c.from} ${c.to} transition-all ${
                        avatarIdx === i
                          ? "ring-2 ring-white scale-110"
                          : "opacity-80 hover:opacity-100"
                      }`}
                    />
                  ))}
                </div>
              </div>
              <label
                className={`flex items-start gap-3 px-3 py-3 rounded-lg border cursor-pointer transition-colors ${
                  isKids
                    ? "border-cyan-400/60 bg-cyan-400/10"
                    : "border-white/10 hover:border-white/20 hover:bg-white/5"
                }`}
              >
                <input
                  type="checkbox"
                  data-testid="profile-kids-toggle"
                  checked={isKids}
                  onChange={(e) => {
                    setIsKids(e.target.checked);
                    if (e.target.checked) setPin("");
                  }}
                  className="mt-0.5 accent-cyan-400"
                />
                <div className="flex-1">
                  <div className="text-sm font-semibold flex items-center gap-1.5">
                    <ShieldCheck className="w-3.5 h-3.5 text-cyan-400" />
                    Kids profile
                  </div>
                  <div className="text-xs text-slate-400 mt-0.5">
                    Hides adult / mature content from Live TV, Movies and Series.
                  </div>
                </div>
              </label>

              {!isKids && (
                <div>
                  <label className="block text-xs uppercase tracking-wider text-slate-400 mb-2 flex items-center gap-1.5">
                    <Lock className="w-3 h-3 text-amber-300" />
                    PIN (optional, 4 digits)
                  </label>
                  <input
                    data-testid="profile-pin-input"
                    type="password"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    maxLength={4}
                    value={pin}
                    onChange={(e) => setPin(e.target.value.replace(/\D/g, ""))}
                    placeholder="Leave blank for none"
                    className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2.5 font-mono tracking-widest text-center text-white placeholder:text-slate-600 placeholder:tracking-normal focus:outline-none focus:border-amber-400/60"
                  />
                  <p className="text-[11px] text-slate-500 mt-1">
                    Required to switch back from a kids profile to this one.
                  </p>
                </div>
              )}

              {error && (
                <div className="text-sm bg-red-500/10 border border-red-500/30 text-red-300 rounded-lg px-3 py-2">
                  {error}
                </div>
              )}
              <button
                type="submit"
                data-testid="profile-create-confirm"
                disabled={busy}
                className="w-full bg-gradient-to-r from-orange-500 to-pink-500 hover:from-orange-400 hover:to-pink-400 disabled:opacity-60 text-white font-semibold rounded-lg py-2.5"
              >
                {busy ? "Saving…" : "Create profile"}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Multi-profile / connections notice */}
      {showNotice && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm px-4">
          <div
            data-testid="profile-connections-notice"
            className="bg-[#13161d] border border-amber-500/40 rounded-2xl shadow-2xl w-full max-w-md p-6"
          >
            <div className="flex items-start gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-amber-400/15 border border-amber-400/40 flex items-center justify-center shrink-0">
                <AlertTriangle className="w-5 h-5 text-amber-400" />
              </div>
              <div>
                <h3 className="text-lg font-semibold">Heads up</h3>
                <p className="text-sm text-slate-400 mt-0.5">About profiles &amp; connections</p>
              </div>
            </div>
            <p className="text-sm text-slate-200 leading-relaxed">
              You can create up to <span className="font-semibold text-amber-300">{MAX_PROFILES} profiles</span>, but
              this does <span className="font-semibold">not</span> allow you to watch on
              {" "}{MAX_PROFILES} devices at the same time.
              You can only watch on the total connections you ordered.
            </p>
            <button
              data-testid="profile-notice-ok"
              onClick={acknowledgeNotice}
              className="mt-5 w-full bg-amber-500 hover:bg-amber-400 text-black font-semibold rounded-lg py-2.5"
            >
              Got it
            </button>
          </div>
        </div>
      )}
      {/* PIN gate (leaving kids profile → PIN-protected adult profile) */}
      {pinGateProfile && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm px-4">
          <form
            onSubmit={submitPinGate}
            data-testid="profile-pin-gate"
            className="bg-[#13161d] border border-amber-400/40 rounded-2xl shadow-2xl w-full max-w-sm p-6"
          >
            <div className="flex items-start gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-amber-400/15 border border-amber-400/40 flex items-center justify-center shrink-0">
                <Lock className="w-5 h-5 text-amber-400" />
              </div>
              <div>
                <h3 className="text-lg font-semibold">Enter PIN</h3>
                <p className="text-sm text-slate-400 mt-0.5">
                  Enter the PIN for <span className="font-semibold text-slate-200">{pinGateProfile.name}</span>
                </p>
              </div>
            </div>
            <input
              autoFocus
              data-testid="profile-pin-gate-input"
              type="password"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={4}
              value={pinAttempt}
              onChange={(e) => {
                setPinAttempt(e.target.value.replace(/\D/g, ""));
                setPinError("");
              }}
              placeholder="••••"
              className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-3 font-mono tracking-[0.4em] text-center text-xl text-white placeholder:text-slate-600 focus:outline-none focus:border-amber-400/60"
            />
            {pinError && (
              <div className="text-xs text-red-300 mt-2 text-center">{pinError}</div>
            )}
            <div className="grid grid-cols-2 gap-2 mt-5">
              <button
                type="button"
                onClick={() => setPinGateProfile(null)}
                className="bg-white/5 hover:bg-white/10 border border-white/10 rounded-lg py-2.5 text-sm"
              >
                Cancel
              </button>
              <button
                type="submit"
                data-testid="profile-pin-gate-submit"
                disabled={pinAttempt.length !== 4}
                className="bg-amber-500 hover:bg-amber-400 disabled:opacity-50 disabled:cursor-not-allowed text-black font-semibold rounded-lg py-2.5"
              >
                Unlock
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Manage PIN dialog (from Manage mode) */}
      {managePinProfile && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm px-4">
          <form
            onSubmit={(e) => submitManagePin(e, "save")}
            className="bg-[#13161d] border border-white/10 rounded-2xl shadow-2xl w-full max-w-sm p-6"
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold flex items-center gap-2">
                <KeyRound className="w-4 h-4 text-amber-300" />
                PIN for {managePinProfile.name}
              </h3>
              <button
                type="button"
                onClick={() => setManagePinProfile(null)}
                className="p-1 rounded hover:bg-white/10 text-slate-400"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <p className="text-xs text-slate-400 mb-3">
              4-digit PIN required to switch out of a kids profile back to this one. Leave empty to remove.
            </p>
            <input
              autoFocus
              data-testid="manage-pin-input"
              type="password"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={4}
              value={managePinValue}
              onChange={(e) => {
                setManagePinValue(e.target.value.replace(/\D/g, ""));
                setManagePinError("");
              }}
              placeholder="••••"
              className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-3 font-mono tracking-[0.4em] text-center text-xl text-white placeholder:text-slate-600 focus:outline-none focus:border-amber-400/60"
            />
            {managePinError && (
              <div className="text-xs text-red-300 mt-2 text-center">{managePinError}</div>
            )}
            <div className="grid grid-cols-2 gap-2 mt-5">
              {hasPin(managePinProfile) && (
                <button
                  type="button"
                  data-testid="manage-pin-clear"
                  onClick={(e) => submitManagePin(e, "clear")}
                  className="col-span-2 bg-red-500/15 hover:bg-red-500/25 border border-red-500/30 text-red-300 rounded-lg py-2 text-sm"
                >
                  Remove PIN
                </button>
              )}
              <button
                type="button"
                onClick={() => setManagePinProfile(null)}
                className="bg-white/5 hover:bg-white/10 border border-white/10 rounded-lg py-2.5 text-sm"
              >
                Cancel
              </button>
              <button
                type="submit"
                data-testid="manage-pin-save"
                disabled={managePinValue.length !== 4 && managePinValue.length !== 0}
                className="bg-amber-500 hover:bg-amber-400 disabled:opacity-50 disabled:cursor-not-allowed text-black font-semibold rounded-lg py-2.5"
              >
                Save PIN
              </button>
            </div>
          </form>
        </div>
      )}

      {/* v1.3.72 — "profile in use" modal (v1.3.80 polish:
          holder chip + retry countdown + Escape-to-close). */}
      {inUseProfile && (
        <div
          data-testid="profile-in-use-modal"
          className="fixed inset-0 z-50 bg-black/85 flex items-center justify-center px-4 backdrop-blur-sm"
          onClick={_closeInUse}
        >
          <div
            className="bg-[#11141b] border border-amber-500/40 rounded-2xl max-w-md w-full p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-full bg-amber-500/15 flex items-center justify-center shrink-0">
                <AlertTriangle className="w-5 h-5 text-amber-400" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-bold mb-1">Profile in use</h3>
                <p className="text-sm text-slate-300 leading-relaxed">
                  <span className="font-semibold text-white">{inUseProfile.name}</span>
                  {" "}is currently active on another device
                  {inUseHolderTag && (
                    <>
                      {" "}
                      <span
                        data-testid="profile-in-use-holder-tag"
                        className="inline-block ml-1 px-2 py-0.5 rounded bg-amber-500/15 text-amber-300 font-mono text-[11px] tracking-wider"
                      >
                        …{inUseHolderTag}
                      </span>
                    </>
                  )}
                  .
                </p>
                <p className="text-xs text-slate-500 mt-3">
                  The lock automatically releases after 5 minutes of inactivity on
                  the other device
                  {_inUseSecondsLeft > 0 && (
                    <>
                      {" — you can retry in "}
                      <span
                        data-testid="profile-in-use-countdown"
                        className="text-amber-300 font-mono"
                      >
                        {Math.floor(_inUseSecondsLeft / 60)}:
                        {String(_inUseSecondsLeft % 60).padStart(2, "0")}
                      </span>
                    </>
                  )}
                  .
                </p>
              </div>
            </div>
            <div className="flex gap-2 mt-5">
              <button
                data-testid="profile-in-use-retry"
                onClick={retryInUseClaim}
                disabled={inUseRetrying}
                className="flex-1 bg-white/10 hover:bg-white/15 disabled:opacity-40 text-white text-sm font-semibold rounded-lg py-2.5 flex items-center justify-center gap-2"
              >
                {inUseRetrying && <Loader2 className="w-4 h-4 animate-spin" />}
                Try again
              </button>
              <button
                data-testid="profile-in-use-different"
                onClick={_closeInUse}
                className="flex-1 bg-white/10 hover:bg-white/15 text-white text-sm font-semibold rounded-lg py-2.5"
              >
                Pick different
              </button>
              <button
                data-testid="profile-in-use-create"
                onClick={() => {
                  _closeInUse();
                  onOpenCreate();
                }}
                disabled={profiles.length >= MAX_PROFILES}
                className="flex-1 bg-orange-500 hover:bg-orange-400 disabled:opacity-40 disabled:cursor-not-allowed text-white text-sm font-semibold rounded-lg py-2.5"
              >
                Create new
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
